import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Grid, TextField, Button, Checkbox, FormControlLabel,
  Stack, Alert, InputAdornment
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';

import {
  createSupplier,
  updateSupplier,
  getSupplierById,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const zen = {
  blue: '#1976d2',
  blueDark: '#155fa0',
  sectionTitleSx: {
    background: '#1976d2',
    color: '#fff',
    fontWeight: 800,
    letterSpacing: 0.3,
    px: 2,
    py: 1,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  sectionCardSx: {
    borderRadius: 2,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
    backgroundColor: 'background.paper'
  },
};
function Section({ title, children, sx }) {
  return (
    <Box sx={{ ...zen.sectionCardSx, ...sx }}>
      <Box sx={zen.sectionTitleSx}>{title}</Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}
const onlyDigits = (v) => String(v || '').replace(/\D/g, '');

export default function FormFornecedor({
  isEditMode = false,
  supplierId = null,
  embedded = false,
  onSaved,
  onCancel,
}) {
  const params = useParams();
  const navigate = useNavigate();

  const id = supplierId ?? params.id;
  const editing = isEditMode || !!supplierId;

  const [form, setForm] = useState({
    cnpj: '',
    inscricaoEstadual: '',
    razaoSocial: '',
    nomeFantasia: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cep: '',
    cidade: '',
    estado: '',
    email: '',
    telefone: '',
    simplesNacional: false,
  });

  const [saving, setSaving] = useState(false);
  const [loadingCnpj, setLoadingCnpj] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const titulo = useMemo(
    () => (editing ? `Editar Fornecedor #${id}` : 'Cadastro de Fornecedor'),
    [editing, id]
  );

  useEffect(() => {
    (async () => {
      if (!editing) return;
      try {
        const { data } = await getSupplierById(id);
        setForm({
          cnpj: data.cnpj || '',
          inscricaoEstadual: data.inscricaoEstadual || '',
          razaoSocial: data.razaoSocial || '',
          nomeFantasia: data.nomeFantasia || '',
          logradouro: data.logradouro || '',
          numero: data.numero || '',
          complemento: data.complemento || '',
          bairro: data.bairro || '',
          cep: data.cep || '',
          cidade: data.cidade || '',
          estado: data.estado || '',
          email: data.email || '',
          telefone: data.telefone || '',
          simplesNacional: !!data.simplesNacional,
        });
      } catch (e) {
        setErrorMessage(getApiErrorMessage(e, 'Não foi possível carregar o fornecedor.'));
      }
    })();
  }, [editing, id]);

  const fechar = () => {
    if (onCancel) onCancel();
    else navigate('/suppliers');
  };

  const onChange = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  // === Buscar CNPJ (idêntico ao do cliente) ===
  const consultarCnpj = async () => {
    const cnpj = onlyDigits(form.cnpj);
    if (cnpj.length !== 14) {
      setErrorMessage('CNPJ inválido.');
      return;
    }
    setErrorMessage('');
    setLoadingCnpj(true);
    try {
      const resp = await fetch(`https://publica.cnpj.ws/cnpj/${cnpj}`);
      if (!resp.ok) throw new Error('CNPJ não encontrado');
      const data = await resp.json();

      const est = data?.estabelecimento || {};
      const primeiraIE = est?.inscricoes_estaduais?.[0]?.inscricao_estadual || '';

      setForm((prev) => ({
        ...prev,
        razaoSocial: data?.razao_social || prev.razaoSocial,
        nomeFantasia: est?.nome_fantasia || prev.nomeFantasia,
        logradouro: est?.logradouro || prev.logradouro,
        numero: est?.numero || prev.numero,
        complemento: est?.complemento || prev.complemento,
        bairro: est?.bairro || prev.bairro,
        cep: est?.cep || prev.cep,
        cidade: est?.cidade?.nome || prev.cidade,
        estado: est?.estado?.sigla || prev.estado,
        email: est?.email || prev.email,
        telefone: est?.telefone1 || prev.telefone,
        inscricaoEstadual: primeiraIE || prev.inscricaoEstadual,
        simplesNacional: (data?.simples?.simples || '').toLowerCase() === 'sim',
      }));
    } catch (e) {
      setErrorMessage(e.message || 'Erro ao consultar CNPJ.');
    } finally {
      setLoadingCnpj(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSaving(true);
    try {
      if (!onlyDigits(form.cnpj)) throw new Error('CNPJ é obrigatório.');
      if (!form.razaoSocial?.trim()) throw new Error('Razão Social é obrigatória.');
      if (!form.logradouro?.trim() || !form.numero?.trim() || !form.bairro?.trim()) {
        throw new Error('Endereço incompleto (logradouro, número, bairro).');
      }
      if (!form.cidade?.trim() || !form.estado?.trim() || !form.cep?.trim()) {
        throw new Error('Cidade/Estado/CEP são obrigatórios.');
      }

      const payload = {
        id: editing ? Number(id) : 0,
        cnpj: onlyDigits(form.cnpj),
        inscricaoEstadual: form.inscricaoEstadual || null,
        razaoSocial: form.razaoSocial,
        nomeFantasia: form.nomeFantasia || null,
        logradouro: form.logradouro,
        numero: form.numero,
        complemento: form.complemento || null,
        bairro: form.bairro,
        cep: onlyDigits(form.cep),
        cidade: form.cidade,
        estado: form.estado,
        email: form.email || null,
        telefone: onlyDigits(form.telefone) || null,
        simplesNacional: !!form.simplesNacional,
      };

      if (editing) {
        await updateSupplier(id, payload);
      } else {
        await createSupplier(payload);
      }
      if (onSaved) onSaved(editing ? 'Fornecedor atualizado' : 'Fornecedor criado');
      else navigate('/suppliers');
    } catch (err) {
      setErrorMessage(getApiErrorMessage(err, 'Erro ao salvar.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{ p: embedded ? 0 : 2, maxWidth: embedded ? 'none' : 1200, mx: 'auto' }}
      component="form"
      onSubmit={onSubmit}
    >
      {!embedded && (
        <Typography variant="h5" sx={{ textAlign: 'center', color: zen.blue, fontWeight: 900, mb: 2 }}>
          {titulo}
        </Typography>
      )}

      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

      <Section title="Identificação" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="CNPJ"
              value={form.cnpj}
              onChange={onChange('cnpj')}
              fullWidth
              placeholder="00.000.000/0000-00"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={consultarCnpj}
                      variant="contained"
                      size="small"
                      disabled={loadingCnpj}
                      sx={{ textTransform: 'none' }}
                    >
                      {loadingCnpj ? 'Buscando...' : 'Buscar CNPJ'}
                    </Button>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Inscrição Estadual"
              value={form.inscricaoEstadual}
              onChange={onChange('inscricaoEstadual')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={<Checkbox checked={form.simplesNacional} onChange={onChange('simplesNacional')} />}
              label="Simples Nacional"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField label="Razão Social" value={form.razaoSocial} onChange={onChange('razaoSocial')} fullWidth required />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Nome Fantasia" value={form.nomeFantasia} onChange={onChange('nomeFantasia')} fullWidth />
          </Grid>
        </Grid>
      </Section>

      <Section title="Endereço" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Logradouro" value={form.logradouro} onChange={onChange('logradouro')} fullWidth required />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="Número" value={form.numero} onChange={onChange('numero')} fullWidth required />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Complemento" value={form.complemento} onChange={onChange('complemento')} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Bairro" value={form.bairro} onChange={onChange('bairro')} fullWidth required />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="CEP" value={form.cep} onChange={onChange('cep')} fullWidth placeholder="00000-000" required />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Cidade" value={form.cidade} onChange={onChange('cidade')} fullWidth required />
          </Grid>
          <Grid item xs={12} md={1}>
            <TextField label="UF" value={form.estado} onChange={onChange('estado')} fullWidth inputProps={{ maxLength: 2 }} required />
          </Grid>
        </Grid>
      </Section>

      <Section title="Contato">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Email" type="email" value={form.email} onChange={onChange('email')} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Telefone" value={form.telefone} onChange={onChange('telefone')} fullWidth placeholder="(00) 00000-0000" />
          </Grid>
        </Grid>
      </Section>

      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
        <Button
          id="btn-save"
          type="submit"
          variant="contained"
          disabled={saving}
          startIcon={<SaveIcon />}
          sx={{ minWidth: 160, background: zen.blue, '&:hover': { background: zen.blueDark }, textTransform: 'none', fontWeight: 700 }}
        >
          {editing ? 'Salvar Alterações' : 'Cadastrar Fornecedor'}
        </Button>
        <Button
          variant="contained"
          color="inherit"
          onClick={fechar}
          startIcon={<CancelIcon />}
          sx={{ minWidth: 160, textTransform: 'none', fontWeight: 700 }}
        >
          Cancelar
        </Button>
      </Stack>
    </Box>
  );
}
